import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { ConfigurationsService } from '../../services/configurations.service';
import { UsersService, User } from '../../services/users.service';
import { AlertService } from '../alert-service/alert-service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-modal-create-edit',
  standalone: true,
  imports: [FormsModule, FontAwesomeModule, TranslateModule, ButtonComponent, NgSelectModule],
  templateUrl: './modal-create-edit.component.html',
  styleUrls: ['./modal-create-edit.component.scss']
})
export class ModalCreateEditComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() isEdit = false;
  @Input() title = '';
  @Input() icon: IconDefinition | null = null;
  @Input() configuration: any = null;
  @Input() configType: string = '';
  @Input() configForm: any = {};
  @Input() prefillValue: Record<string, any> | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() updated = new EventEmitter<void>();

  faTimes = faTimes;
  faCheck = faCheck;
  loading = false;
  users: User[] = [];
  currentUserId: string | null = null;

  private meRequested = false;
  private preselectApplied = false;

  constructor(
    private configService: ConfigurationsService,
    private usersService: UsersService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadUsersIfNeeded();
    this.loadCurrentUserIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['configuration'] || changes['isEdit'] || changes['configType'] || changes['prefillValue']) {
      this.initForm();
      this.loadUsersIfNeeded();
      this.loadCurrentUserIfNeeded();
    }
  }

  private loadUsersIfNeeded(): void {
    if (!this.isEdit && !!this.configType && this.users.length === 0) {
      this.usersService.list().subscribe({
        next: (res) => { this.users = res.users || []; },
        error: () => { this.users = []; },
      });
    }
  }

  private loadCurrentUserIfNeeded(): void {
    if (this.isEdit || !this.configType || this.meRequested) return;

    this.meRequested = true;
    this.usersService.getMe().subscribe({
      next: (res) => {
        this.currentUserId = res.user?.id ?? null;
        this.applyCurrentUserPreselection();
      },
      error: () => { this.currentUserId = null; },
    });
  }

  private applyCurrentUserPreselection(): void {
    if (this.isEdit || !this.currentUserId || this.preselectApplied) return;
    if (!Array.isArray(this.configForm?.allowed_user_ids)) return;

    if (!this.configForm.allowed_user_ids.includes(this.currentUserId)) {
      this.configForm.allowed_user_ids = [...this.configForm.allowed_user_ids, this.currentUserId];
    }
    this.preselectApplied = true;
  }

  private defaultAllowedUserIds(): string[] {
    return this.currentUserId ? [this.currentUserId] : [];
  }

  initForm() {
    if (this.isEdit && this.configuration) {
      const config = this.configuration;
      if (config.type === 'di2winocr_features') {
        this.configForm = {
          name: config.key || '',
          display_name: config.name ?? config.key ?? '',
          layoutAnalysis: config.value?.layoutAnalysis != null ? String(config.value.layoutAnalysis) : '',
          inputType: config.value?.inputType ?? '',
          ocrType: config.value?.ocrType ?? '',
          outputType: config.value?.outputType ?? '',
          textType: config.value?.textType ?? 'mix',
          ocrCrop: config.value?.ocrCrop ?? false,
          autoCut: config.value?.autoCut ?? false,
          enhanceDocument: config.value?.enhanceDocument ?? false,
          sortOcr: config.value?.sortOcr ?? false,
          adjustRotation: typeof config.value?.adjustRotation === 'string' ? config.value.adjustRotation : '',
          splitPdf: config.value?.splitPdf ?? false
        };
      } else if (config.type === 'di2winocr_server' || config.type === 'delfos_server') {
        this.configForm = {
          name: config.key || '',
          url: config.value?.url ?? ''
        };
      } else if (config.type === 'delfos_llm') {
        this.configForm = {
          name: config.key || '',
          llm_model: config.value?.llm_model ?? '',
          thinking: config.value?.thinking ?? false
        };
      } else if (config.type === 'retriever') {
        this.configForm = {
          name: config.key || '',
          retriever: config.value?.retriever ?? ''
        };
      } else {
        this.configForm = { name: config.key || '', ...config.value };
      }
    } else if (!this.isEdit) {
      this.configForm = {};
      this.preselectApplied = !!this.currentUserId;
      if (this.configType === 'delfos_server' || this.configType === 'di2winocr_server') {
        this.configForm = { name: '', url: '', allowed_user_ids: this.defaultAllowedUserIds() };
      } else if (this.configType === 'delfos_llm') {
        this.configForm = { name: '', llm_model: '', thinking: false, allowed_user_ids: this.defaultAllowedUserIds() };
      } else if (this.configType === 'retriever') {
        this.configForm = { name: '', retriever: '', allowed_user_ids: this.defaultAllowedUserIds() };
      } else if (this.configType === 'di2winocr_features') {
        const pv = this.prefillValue || {};
        this.configForm = {
          name: '',
          display_name: '',
          layoutAnalysis: pv['layoutAnalysis'] != null ? String(pv['layoutAnalysis']) : '',
          inputType: pv['inputType'] ?? '',
          ocrType: pv['ocrType'] ?? '',
          outputType: pv['outputType'] ?? '',
          textType: pv['textType'] ?? 'mix',
          ocrCrop: pv['ocrCrop'] ?? false,
          autoCut: pv['autoCut'] ?? false,
          enhanceDocument: pv['enhanceDocument'] ?? false,
          sortOcr: pv['sortOcr'] ?? false,
          adjustRotation: typeof pv['adjustRotation'] === 'string' ? pv['adjustRotation'] : '',
          splitPdf: pv['splitPdf'] ?? false,
          allowed_user_ids: this.defaultAllowedUserIds()
        };
      }
    }
  }

  onInputChange() {}

  onClose() {
    this.close.emit();
  }

  onSave() {
    if (!this.configType || !this.isFormValid()) {
      this.alertService.showError(this.translate.instant('CONFIGURATIONS.ERRORS.FILL_REQUIRED'));
      return;
    }
    this.loading = true;
    if (this.isEdit) {
      const configId = this.configuration?.id;
      if (!configId) {
        this.loading = false;
        this.alertService.showError(this.translate.instant('CONFIGURATIONS.ERRORS.ID_NOT_FOUND'));
        return;
      }
      this.configService.update(this.configType, configId, this.configForm).subscribe({
        next: () => {
          this.loading = false;
          this.alertService.showSuccess(this.translate.instant('CONFIGURATIONS.UPDATE.SUCCESS'));
          this.updated.emit();
          this.onClose();
        },
        error: (err) => {
          this.loading = false;
          this.alertService.showError(err);
        }
      });
    } else {
      this.configService.create(this.configType, this.configForm).subscribe({
        next: () => {
          this.loading = false;
          this.alertService.showSuccess(this.translate.instant('CONFIGURATIONS.CREATE.SUCCESS'));
          this.updated.emit();
          this.onClose();
        },
        error: (err) => {
          this.loading = false;
          this.alertService.showError(err);
        }
      });
    }
  }

  private static readonly REQUIRED_FIELDS_BY_TYPE: Record<string, string[]> = {
    delfos_server: ['name', 'url'],
    di2winocr_server: ['name', 'url'],
    delfos_llm: ['name', 'llm_model'],
    di2winocr_features: ['name', 'display_name', 'inputType', 'ocrType', 'outputType', 'textType'],
    retriever: ['name', 'retriever'],
  };

  private isFormValid(): boolean {
    const requiredFields = ModalCreateEditComponent.REQUIRED_FIELDS_BY_TYPE[this.configType]
      ?? Object.keys(this.configForm);
    return requiredFields.every(key => {
      const v = this.configForm[key];
      return v !== null && v !== undefined && v !== '';
    });
  }
}